import { reactive } from 'vue'

/**
 * Estado singleton compartilhado com ImoConfirm.vue
 * Não exportar diretamente para o restante da aplicação —
 * apenas ImoConfirm.vue deve importar confirmState.
 */
export const confirmState = reactive({
  show: false,
  options: {},
  resolve: null,
  mounted: false, // true quando ImoConfirm está montado no App.vue
})

/**
 * Variantes com estilo definido no ImoButton. O `validators` de lá está com o
 * nome errado (o correto é `validator`), então valor inválido não gera aviso —
 * apenas vira uma classe CSS inexistente e o botão sai sem estilo.
 *
 * @typedef {'primary'|'secondary'|'tertiary'|'special'|'error'} Variant
 */

/**
 * Exibe um diálogo de confirmação e retorna o valor da ação escolhida.
 *
 * @param {object} options
 * @param {string} [options.title]
 * @param {string} [options.message]
 * @param {string} [options.confirmLabel]
 * @param {any} [options.confirmValue=true]
 * @param {string} [options.confirmIcon] Ícone do botão de confirmar (ex.: 'arrow-right-02')
 * @param {'left'|'right'} [options.confirmIconPosition='right']
 * @param {'sm'|'md'|'lg'} [options.confirmSize] Tamanho do botão de confirmar
 * @param {Variant} [options.confirmVariant='primary']
 * @param {string} [options.cancelLabel]
 * @param {any} [options.cancelValue=false]
 * @param {string|null} [options.cancelIcon='undo-arrow'] Passe `null` para não exibir ícone
 * @param {'left'|'right'} [options.cancelIconPosition='left']
 * @param {Variant} [options.cancelVariant='quaternary'] O default não tem estilo no
 *   ImoButton — é o visual sem borda que o cancelar já tinha
 * @param {string} [options.extraActionLabel]
 * @param {any} [options.extraActionValue='extra']
 * @param {Variant} [options.extraActionVariant='secondary']
 * @param {string} [options.extraActionIcon] Ícone do botão de ação extra
 * @param {'left'|'right'} [options.extraActionIconPosition='right']
 * @param {'warning'|'danger'|'info'|'success'} [options.variant]
 * @param {boolean} [options.hideCancelButton]
 * @returns {Promise<any>}
 *
 * @example
 * // Confirmação destrutiva
 * const ok = await confirm({
 *   title: 'Excluir recebível?',
 *   message: 'Esta ação não pode ser desfeita.',
 *   variant: 'danger',
 *   confirmLabel: 'Sim, excluir',
 * })
 * if (!ok) return
 *
 * @example
 * // setBeforeClose
 * setBeforeClose(async () => {
 *   if (!hasChanges.value) return true
 *   return await confirm({ title: 'Descartar alterações?', variant: 'warning' })
 * })
 */
export function confirm(options = {}) {
  // Fallback se ImoConfirm não estiver montado
  if (!confirmState.mounted) {
    const parts = [options.title, options.message].filter(Boolean)
    return Promise.resolve(
      window.confirm(parts.join('\n\n'))
        ? (options.confirmValue ?? true)
        : (options.cancelValue ?? false),
    )
  }

  return new Promise(resolve => {
    confirmState.options = options
    confirmState.show = true
    confirmState.resolve = resolve
  })
}
